import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'

export type AxiosReqResult = AxiosRequestConfig | undefined
export type AxiosResResult = AxiosResponse | undefined
export abstract class HttpHandler {
  public abstract reqHandle(req: AxiosRequestConfig): AxiosReqResult
  public abstract resHandle(res: AxiosResponse): AxiosResResult
  public abstract errorHandle(error: AxiosError): AxiosError
}

export interface HttpInterceptor {
  req(req: AxiosRequestConfig, next: HttpHandler): AxiosReqResult
  res(res: AxiosResponse, next: HttpHandler): AxiosResResult
  error(error: AxiosError, next: HttpHandler): AxiosError
}

export class HttpInterceptorInstance implements HttpInterceptor {
  public req(req: any, next: HttpHandler): AxiosReqResult {
    return next.reqHandle(req)
  }
  public res(res: any, next: HttpHandler): AxiosResResult {
    return next.resHandle(res)
  }
  public error(error: AxiosError, next: HttpHandler): AxiosError {
    return next.errorHandle(error)
  }
}
export class HttpInterceptorHandler implements HttpHandler {
  constructor(
    private next: HttpHandler,
    private interceptor: HttpInterceptor
  ) {}

  public reqHandle(req: AxiosRequestConfig): AxiosReqResult {
    return this.interceptor.req(req, this.next)
  }
  public resHandle(res: AxiosResponse): AxiosResResult {
    return this.interceptor.res(res, this.next)
  }
  public errorHandle(error: AxiosError): AxiosError {
    return this.interceptor.error(error, this.next)
  }
}

export abstract class HttpBackend implements HttpHandler {
  public abstract reqHandle(req: AxiosRequestConfig): AxiosRequestConfig
  public abstract resHandle(res: AxiosResponse): AxiosResponse
  public abstract errorHandle(error: AxiosError): AxiosError
}

export class HttpInterceptingHandler implements HttpHandler {
  private chain: any = null

  constructor(private backend: HttpBackend, private injector: any[] = []) {}
  public initChain() {
    if (this.chain === null) {
      const interceptors = this.injector
      this.chain = interceptors.reduceRight(
        (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
        this.backend
      )
    }
  }
  public reqHandle(req: AxiosRequestConfig): AxiosRequestConfig {
    this.initChain()
    return this.chain.reqHandle(req)
  }
  public resHandle(res: AxiosResponse): AxiosResponse {
    this.initChain()
    return this.chain.resHandle(res)
  }
  public errorHandle(error: AxiosError): AxiosError {
    this.initChain()
    return this.chain.errorHandle(error)
  }
}

export function interceptingHandler(
  backend: any,
  interceptors: HttpInterceptor[] | null = []
): HttpHandler {
  if (!interceptors) {
    return backend
  }
  return interceptors.reduceRight(
    (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
    backend
  )
}

class Backend extends HttpBackend {
  public reqHandle(req: AxiosRequestConfig): AxiosRequestConfig {
    return req
  }
  public resHandle(res: AxiosResponse): AxiosResponse {
    return res
  }
  public errorHandle(error: AxiosError): AxiosError {
    return error
  }
}

const bankend = new Backend()

type CurriedFunction<T1, T2, R> = (t1?: T1) => (...args: T2[]) => R

function curry2<T1, T2, R>(
  f: (t1: T1 | undefined, t2: T2[] | undefined) => R
): CurriedFunction<T1, T2, R> {
  function curriedFunction(t1?: T1): any {
    return function(...args: T2[]): R {
      return f(t1, args)
    }
  }

  return curriedFunction
}

export const Interceptor = curry2(
  (defaultConfig: any = {}, linkedList?: HttpInterceptor[]): AxiosInstance => {
    const instance: AxiosInstance = axios.create(defaultConfig)
    if(linkedList) linkedList = [...linkedList]
    const httpInterceptingHandler = new HttpInterceptingHandler(
      bankend,
      linkedList
    )
    instance.interceptors.request.use(
      function(config) {
        return httpInterceptingHandler.reqHandle(config)
      },
      function(error) {
        // 对请求错误做些什么
        return error
      }
    )

    // 添加响应拦截器
    instance.interceptors.response.use(
      function(response) {
        // 对响应数据做点什么
        return httpInterceptingHandler.resHandle(response)
      },
      function(error) {
        return httpInterceptingHandler.errorHandle(error)
      }
    )
    return instance
  }
)
